import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';

// Session 11: every link into a client's pages carries ?client=<client_id>.
// The agency Inbox is cross-client, so a bare /blogs/<slug>/review would open
// under whichever client happened to be active (usually Digital Lotus) and
// show nothing. The client role ignores the param, so it is always safe.
export function clientPath(path, clientId) {
  if (!path || !clientId) return path || '/';
  const [base, hash] = path.split('#');
  const [pathname, search = ''] = base.split('?');
  const sp = new URLSearchParams(search);
  sp.set('client', clientId);
  return `${pathname}?${sp.toString()}${hash ? `#${hash}` : ''}`;
}

// Same, bound to the active client: `const to = useClientPath(); to('/blogs')`.
export function useClientPath() {
  const { activeId } = useAuth();
  return useCallback((path) => clientPath(path, activeId), [activeId]);
}

// Session 11: a slug page (draft review, questions, post reader) opened by an
// agency user without ?client= whose slug does not exist under the active
// client switches to the client that owns it, instead of showing empty.
// `view` is the v_portal_* view the page reads; `notFound` is true once the
// page's own lookup came back empty. Only acts when the client was implied
// (filled in by the portal), never against an explicit ?client= or a switcher
// choice, and only when exactly one other client owns the slug.
export function useSwitchToOwningClient(view, slug, notFound) {
  const { isAgency, activeId, clientImplied, clients, setActiveId } = useAuth();
  useEffect(() => {
    if (!notFound || !isAgency || !clientImplied || !slug || !activeId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from(view).select('client_id').eq('slug', slug).neq('client_id', activeId);
      if (cancelled || error || !data) return;
      const owners = [...new Set(data.map((r) => r.client_id))]
        .filter((id) => clients.some((c) => c.client_id === id));
      if (owners.length === 1) setActiveId(owners[0]);
    })();
    return () => { cancelled = true; };
  }, [view, slug, notFound, isAgency, clientImplied, activeId, clients, setActiveId]);
}

// Session 17: agency pages keyed by a client in the path (/admin/clients/:id/...)
// or by one client's item (/admin/plans/:jobId) follow the switcher. When the
// switcher picks a different client than the one the page shows, go to
// pathFor(newClientId) for that client. Only reacts to a change of the active
// client after the page has mounted, so the page's own "make my client active"
// on load never bounces it away.
export function useFollowSwitcher(ownerId, pathFor) {
  const { isAgency, activeId } = useAuth();
  const navigate = useNavigate();
  const prevRef = useRef(activeId);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = activeId;
    if (!isAgency || !ownerId || !activeId || !prev || prev === activeId || activeId === ownerId) return;
    navigate(clientPath(pathFor(activeId), activeId));
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps
}
